// src/pages/health/WorkoutSchedule.js
import { useMemo, useState } from "react";
import { addMonths, subMonths, startOfMonth, format, isSameDay } from "date-fns";
import { ko } from "date-fns/locale";
import Calendar from "react-calendar";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import AuthCard from "../../components/auth/AuthCard";
import BottomNav from "../../components/common/BottomNav";
import { useLoadingGate } from "../../hooks/useLoadingGate";
import { useMonthSummary, useDayWorkouts } from "../../hooks/useWorkouts";
import { useNavigate } from "react-router-dom";

export default function WorkoutSchedule() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(new Date());
    const [month, setMonth] = useState(startOfMonth(new Date()));

    const goPrev = () => setMonth((m) => startOfMonth(subMonths(m, 1)));
    const goNext = () => setMonth((m) => startOfMonth(addMonths(m, 1)));

    // 월 요약(일별 count)
    const { data: summary = [] } = useMonthSummary(month);
    const countMap = useMemo(() => {
        const m = new Map();
        for (const r of summary) m.set(r.date, r.count);
        return m;
    }, [summary]);

    // 선택 날짜의 세부 기록
    const { data: dayWorkouts = [] } = useDayWorkouts(selected);
    const selKey = format(selected, "yyyy-MM-dd");
    const selectedCount = countMap.get(selKey) ?? 0;

    return (
        <main className="
            h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7
            bg-white
        ">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title="운동 스케줄">
                    <div className="flex flex-col flex-1 pt-3 pb-10">
                        <div className="w-full flex items-center justify-between pb-7">
                            <button
                                type="button"
                                onClick={goPrev}
                                className="rounded-md active:scale-95"
                                aria-label="이전 달"
                            >
                                <ChevronLeftIcon className="w-6 h-6 text-gray-400" />
                            </button>
                            <div className="font-extrabold text-center text-2xl md:text-3xl">
                                {format(month, "yyyy년 M월", { locale: ko })}
                            </div>
                            <button
                                type="button"
                                onClick={goNext}
                                className="rounded-md active:scale-95"
                                aria-label="다음 달"
                            >
                                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>

                        <div className="w-full items-center justify-center sc-cal px-1.5">
                            <Calendar
                                activeStartDate={month}
                                onActiveStartDateChange={({ activeStartDate }) =>
                                    setMonth(startOfMonth(activeStartDate))
                                }
                                view="month"
                                formatShortWeekday={(locale, date) => format(date, "EEE", { locale: ko })}
                                value={selected}
                                onClickDay={(d) => setSelected(d)}
                                formatDay={() => ""}
                                showNavigation={false}

                                locale="ko-KR"
                                calendarType="gregory"

                                tileClassName={({ date }) => {
                                    const base = "!flex !flex-col !items-center !justify-center !py-1 !min-h-[56px] md:!py-3 md:!min-h-[64px] focus:outline-none focus-visible:outline-none";
                                    return isSameDay(date, selected)
                                        ? `${base} !bg-white !text-black ring-2 ring-[#5F9BFF] rounded-xl `
                                        : base;
                                }}

                                tileContent={({ date, view }) => {
                                    if (view !== "month") return null;
                                    const key = format(date, "yyyy-MM-dd");
                                    const cnt = countMap.get(key) ?? 0;
                                    return (
                                        <div className="pointer-events-none leading-none flex flex-col items-center font-semibold text-sm md:text-base">
                                            <div className="text-lg md:text-xl">{format(date, "d")}</div>
                                            <div
                                                className={`mt-0.5 md:mt-1 w-2 h-2 rounded-full ${cnt > 0 ? "bg-[#5F9BFF]" : "opacity-0"
                                                    }`}
                                            />
                                        </div>
                                    );
                                }}
                            />

                        </div>

                        <div className="w-full mt-6">
                            <button
                                type="button"
                                disabled={selectedCount === 0}
                                className="w-full h-12 md:h-14 rounded-xl font-semibold bg-white border border-gray-200 disabled:opacity-50 text-[#5F9BFF] text-base md:text-lg"
                                onClick={() => navigate(`/schedule/day?date=${selKey}`)}
                            >
                                {selectedCount > 0
                                    ? `운동 기록 ${selectedCount}건 확인하기`
                                    : "이 날짜의 운동 기록이 없습니다"}
                            </button>
                        </div>
                    </div>
                </AuthCard>
            )}
            <BottomNav className="h-16" />
        </main>
    );
}
